import React, {
	useState,
	useCallback,
	type ChangeEvent,
	type PropsWithChildren,
	Fragment,
} from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql, useMutation } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import type { NumberEditViewProps } from '@atlassian/jira-issue-field-number-editview-full/src/ui/number/types.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import NumberEditViewEntryPoint from '@atlassian/jira-issue-field-number-editview-full/src/entrypoint.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { NumberReadView } from '@atlassian/jira-issue-field-number-readview-full/src/ui/number/index.tsx';
import { transformToServer, transformToUI } from '@atlassian/jira-platform-utils-number';
import type { number_issueFieldNumber_NumberField_Mutation as NumberMutation } from './__generated__/number_issueFieldNumber_NumberField_Mutation.graphql';
import type { number_issueFieldNumberInlineEditFull_NumberInlineEditViewWithIsEditable_fragmentRef$key as NumberWithIsEditableFragment } from './__generated__/number_issueFieldNumberInlineEditFull_NumberInlineEditViewWithIsEditable_fragmentRef.graphql';
import type { number_issueFieldNumberInlineEditFull_NumberInlineEditView_fragmentRef$key as NumberFragment } from './__generated__/number_issueFieldNumberInlineEditFull_NumberInlineEditView_fragmentRef.graphql';
import type {
	NumberValue,
	NumberInlineEditViewWithIsEditableProps,
	NumberInlineEditViewProps,
} from './types.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-number-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `NumberInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [NumberInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const NumberInlineEditViewWithIsEditable = ({
	attributes,
	spacing = 'compact',
	editViewPopup,
	editViewPopupAlignBlock,
	editViewPopupTarget,
	fragmentRef,
	editViewPopupWidth,
	onSubmit,
	onSubmitSucceeded,
	onSubmitFailed,
	isEditable,
	readViewFitContainerHeight,
	isSingleRowHeightEnabled,
}: NumberInlineEditViewWithIsEditableProps): React.JSX.Element => {
	// #region Relay
	const data = useFragment<NumberWithIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment number_issueFieldNumberInlineEditFull_NumberInlineEditViewWithIsEditable_fragmentRef on JiraNumberField
			@argumentDefinitions(
				isNumberFieldConfigurableEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-number-field-configuration-enabled.relayprovider"
				}
			) {
				...number_issueFieldNumberReadviewFull_NumberReadView
				id
				fieldId
				type
				name
				number
				formatConfig @include(if: $isNumberFieldConfigurableEnabled) {
					formatStyle
					formatUnit
				}
			}
		`,
		fragmentRef,
	);
	const { id: uniqueFieldId, fieldId, name: fieldName, type, number, formatConfig } = data;

	const initialValue = fg('frontier-currency-field-fix-percentage-storage')
		? transformToUI(number, formatConfig?.formatStyle)
		: number;

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<NumberMutation>(graphql`
		mutation number_issueFieldNumber_NumberField_Mutation($input: JiraUpdateNumberFieldInput!)
		@raw_response_type {
			jira {
				updateNumberField(input: $input) {
					success
					errors {
						message
					}
					field {
						id
						number
					}
				}
			}
		}
	`);
	// #endregion

	// #region Common state
	const { overriding } = useInlineEditFieldInjections();
	const [updatedValue, setUpdatedValue] = useState<NumberValue>(initialValue);
	const isFieldEditable = overriding.overrideIsEditable(isEditable ?? false);
	// #endregion

	// #region Handle new value
	const handleSubmit = useCallback(
		(newNumber: NumberValue, { onSuccess, onFail }: OnSubmitCallbacks) => {
			const tranformedNumber = fg('frontier-currency-field-fix-percentage-storage')
				? transformToServer(newNumber, formatConfig?.formatStyle)
				: newNumber;

			onSubmit?.(tranformedNumber);

			commit({
				variables: {
					input: {
						id: uniqueFieldId,
						operation: {
							operation: 'SET',
							number: tranformedNumber,
						},
					},
				},

				onCompleted(mutationData) {
					if (mutationData.jira?.updateNumberField?.success) {
						onSuccess();
					} else {
						onFail();
					}
				},
				onError(error) {
					onFail(error);
				},
				optimisticResponse: {
					jira: {
						updateNumberField: {
							success: true,
							errors: null,
							field: {
								id: uniqueFieldId,
								number: tranformedNumber,
							},
						},
					},
				},
			});
		},
		[commit, onSubmit, uniqueFieldId, formatConfig?.formatStyle],
	);

	const getFormatConfigAnalyticsAttributes = useCallback(
		() =>
			formatConfig
				? {
						formatStyle: formatConfig.formatStyle,
						formatUnit: formatConfig.formatUnit,
					}
				: {},
		[formatConfig],
	);

	const {
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChange,
		handleCopy,
		handleCut,
		handlePaste,
		invalidMessage,
		isEditing,
	} = useFieldInlineEditActions({
		attributes: { ...attributes, ...getFormatConfigAnalyticsAttributes() },
		fieldId,
		fieldName,
		fieldType: type,
		initialValue,
		clipboardComponentType: 'numberNullable',
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		updatedValue,
	});

	const onChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const typedValue =
				event.target.value === '' ? null : parseFloat(Number(event.target.value).toFixed(3));
			handleChange(typedValue);
		},
		[handleChange],
	);

	// #region Read view
	const renderReadView = () => (
		<MaybeUFOSegment>
			<NumberReadView fragmentRef={data} isSingleRowHeightEnabled={isSingleRowHeightEnabled} />
		</MaybeUFOSegment>
	);
	// #endregion
	// #region Edit view
	const getEditViewProps = (fieldProps: ValidationFieldProps): NumberEditViewProps => ({
		...fieldProps,
		autoFocus: true,
		label: fieldName,
		defaultValue: updatedValue,
		onChange,
		spacing,
	});
	// #endregion

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupTarget={editViewPopupTarget}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewPopupWidth={editViewPopupWidth}
			editViewPopupMinWidth="small"
			editViewEntryPoint={NumberEditViewEntryPoint}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			onCopy={handleCopy}
			onCut={handleCut}
			onPaste={handlePaste}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
			hasReadViewTruncateDisabled={isSingleRowHeightEnabled}
		/>
	);
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [NumberInlineEditViewProps](./types.tsx)
 */

export const NumberInlineEditView = ({
	fragmentRef,
	...props
}: NumberInlineEditViewProps): React.JSX.Element => {
	const data = useFragment<NumberFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment number_issueFieldNumberInlineEditFull_NumberInlineEditView_fragmentRef on JiraNumberField {
				...number_issueFieldNumberInlineEditFull_NumberInlineEditViewWithIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);

	return (
		<NumberInlineEditViewWithIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
